import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button/Button';
import Modal from 'apollo-react/components/Modal';
import classNames from 'classnames';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import FormFooter from './components/FormFooter';
import { FORM_KEYS, FORM_MODEL_ATTRIBUTES } from './constants/form-manager.constants';
import { FormContext } from './context';
import FormRulesController from './controllers/FormRulesController';
import ValueOverrideController from './controllers/ValueOverrideController';
import DynamicField from './DynamicField';
import FormManager from './FormManager';
import { isFormLoading } from './selectors/common.selectors';
import { getFormKey, getModelByProp } from './selectors/form-manager.selectors';
import VirtualFormManager from './virtual-layout/VirtualFormManager';
import CustomLoader from '../components/CustomLoader';
import { useFormSelector } from '../custom-hooks';
import { useTranslation } from '../custom-hooks/useTranslation';

const useStyles = makeStyles({
  modal: {
    minHeight: 500,
    minWidth: 1200,
  },
  modalWithForm: {
    '& [class*="MuiDialogContent-root"]': {
      marginBottom: 64,
    },
  },
  buttonsWrapper: {
    textAlign: 'right',
    marginTop: 15,
  },
  content: {
    '& > #formManager': {
      padding: '0px 3px',
    },
  },
  buttonGroupeStyle: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    marginLeft: '-24px',
  },
  text: {
    minHeight: 360,
  },
});

// handle all generic form actions
const ModalManager = ({ open, onClose, onSave, identifier, isTableOnly = false }) => {
  const translateString = useTranslation();
  const classes = useStyles();
  const isLoading = useFormSelector(isFormLoading(FORM_KEYS.MODAL));
  const model = useFormSelector(getModelByProp, {
    prop: FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER,
    value: identifier,
  });

  const formKey = useContext(FormContext);
  const formName = useSelector((state) => translateString(getFormKey(state, { formKey })));

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        variant="default"
        hideButtons
        id="enhanced-modal"
        className={classNames(classes.modal, !isTableOnly ? classes.modalWithForm : '')}
        style={{ zIndex: 1200 }}
        disableBackdropClick
        disableEscapeKeyDown
        title={(!isTableOnly && formName) ?? ''}
      >
        <div className={classes.content}>
          {isLoading ? (
            <CustomLoader />
          ) : isTableOnly ? (
            <div>
              {model ? (
                <DynamicField id={model} />
              ) : (
                <p className={classes.text}>
                  {translateString(
                    'Unique identifier of the grid must be defined when enabling "Maximize" feature.'
                  )}
                </p>
              )}
              <div className={classes.buttonsWrapper}>
                <Button variant="text" onClick={onClose} style={{ marginRight: 15 }}>
                  {translateString('Cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    onSave(FORM_KEYS.MODAL);
                  }}
                >
                  {translateString('Save')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <FormManager />
              <VirtualFormManager />
              <FormRulesController />
              <div className={classes.buttonGroupeStyle}>
                <FormFooter onClick={true} />
              </div>
            </>
          )}
        </div>
      </Modal>
      {!isLoading && <ValueOverrideController />}
    </>
  );
};

export default ModalManager;
